import { useState, useEffect, useCallback } from "react";
import { Newspaper } from "lucide-react";
import { api } from "@/services/api";
import { NewsCard, NewsCardSkeleton } from "@/components/NewsCard";
import { SkyEventsWidget, SkyEventsWidgetSkeleton } from "@/components/SkyEventsWidget";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, EmptyState } from "@/components/States";

export default function NewsPage() {
  const [news, setNews] = useState(null);
  const [skyEvents, setSkyEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [newsRes, eventsRes] = await Promise.all([
        api.getNews(20),
        api.getSkyEvents(),
      ]);
      setNews(newsRes.news);
      setSkyEvents(eventsRes.events);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen" data-testid="news-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        {/* Header */}
        <PageHeader 
          icon={Newspaper}
          title="News & Sky Events"
          subtitle="Latest space news and upcoming sky events visible from Florida"
        />

        {error ? (
          <ErrorState message={error} onRetry={fetchData} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main News Column */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="section-title">Space News</h2>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NewsCardSkeleton />
                  <NewsCardSkeleton />
                  <NewsCardSkeleton />
                  <NewsCardSkeleton />
                </div>
              ) : news?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {news.map((article, index) => (
                    <div key={article.id} className="stagger-item" style={{ animationDelay: `${index * 0.05}s` }}>
                      <NewsCard article={article} />
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No News Available"
                  message="Check back later for the latest space news."
                  icon={Newspaper}
                />
              )}
            </div>

            {/* Sidebar - Sky Events */}
            <div className="lg:col-span-1">
              {loading ? (
                <SkyEventsWidgetSkeleton />
              ) : skyEvents?.length > 0 ? (
                <SkyEventsWidget events={skyEvents} />
              ) : null}

              {/* Starlink Info */}
              <div className="glass-card p-6 mt-6">
                <h3 className="section-title mb-4">Starlink Visibility</h3>
                <p className="text-sm text-slate-400 mb-4">
                  SpaceX Starlink satellites are often visible as a "train" of lights shortly after launch. 
                  Best viewing is within 1-2 hours after a Starlink mission from Florida.
                </p>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pro Tip</p>
                  <p className="text-sm text-white">
                    Look toward the southeast horizon 15-20 minutes after launch for the best chance 
                    to see the satellite train.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
